import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ClipboardList, CalendarDays, Wallet, Pencil, HeartHandshake, User, Phone, Mail, Check, X, History, CheckCircle2, XCircle, DollarSign, Activity } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIPO_COLOR = {
  ativo: "bg-[#DFF4F5] text-[#3A9CA2]",
  em_avaliacao: "bg-[#EDE1F3] text-[#7B569A]",
  lista_espera: "bg-[#FBE1EC] text-[#C4547E]",
  inativo: "bg-slate-100 text-slate-600",
};
const STATUS_POS_LABELS = {
  ativo: { label: "Vai iniciar tratamento", color: "bg-emerald-100 text-emerald-700" },
  aguardando_resposta: { label: "Aguardando resposta da família", color: "bg-amber-100 text-amber-700" },
  nao_vai_iniciar: { label: "Não vai iniciar (desistiu)", color: "bg-rose-100 text-rose-700" },
};
const STATUS_MENS = {
  pendente: "bg-amber-100 text-amber-700",
  pago: "bg-emerald-100 text-emerald-700",
  atrasado: "bg-rose-100 text-rose-700",
  cancelado: "bg-slate-100 text-slate-500",
};

function brl(v) { return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0); }
function calcIdade(iso) {
  if (!iso || iso.length < 10) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const n = new Date(y, m - 1, d), t = new Date();
  let i = t.getFullYear() - n.getFullYear();
  const md = t.getMonth() - n.getMonth();
  if (md < 0 || (md === 0 && t.getDate() < n.getDate())) i--;
  return i;
}

export default function PraticanteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [praticante, setPraticante] = useState(null);
  const [sessoes, setSessoes] = useState([]);
  const [sessoesAll, setSessoesAll] = useState([]);
  const [evolucoes, setEvolucoes] = useState([]);
  const [mensalidades, setMensalidades] = useState([]);
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);

  const canViewFicha = user.role === "admin" || user.role === "terapeuta";
  const canEdit = user.role === "admin" || user.role === "recepcionista" || user.role === "terapeuta";
  const canViewFin = user.role === "admin" || user.role === "financeiro" || user.role === "recepcionista";

  const updateStatusPos = async (novo) => {
    try {
      await api.patch(`/praticantes/${id}`, { status_pos_avaliacao: novo });
      setPraticante({ ...praticante, status_pos_avaliacao: novo });
      toast.success("Status atualizado");
    } catch (e) { toast.error("Erro ao atualizar"); }
  };

  useEffect(() => {
    let alive = true;
    const today = new Date().toISOString();
    const load = async () => {
      try {
        const [p, s, sAll, m] = await Promise.all([
          api.get(`/praticantes/${id}`),
          api.get(`/agenda/sessoes`, { params: { praticante_id: id, inicio: today.slice(0, 10) } }).catch(() => ({ data: [] })),
          api.get(`/agenda/sessoes`, { params: { praticante_id: id } }).catch(() => ({ data: [] })),
          canViewFin ? api.get("/financeiro/mensalidades", { params: { praticante_id: id } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        setPraticante(p.data);
        setSessoes(s.data.slice(0, 8));
        setSessoesAll(sAll.data);
        setMensalidades(m.data);
        if (canViewFicha) {
          const [ev, fi] = await Promise.all([
            api.get("/evolucoes", { params: { praticante_id: id } }).catch(() => ({ data: [] })),
            api.get(`/praticantes/${id}/ficha-avaliacao`).catch(() => ({ data: null })),
          ]);
          if (alive) {
            setEvolucoes(ev.data);
            setFicha(fi.data);
          }
        }
      } catch (e) { toast.error("Erro ao carregar praticante"); }
      finally { alive && setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [id, canViewFin, canViewFicha]);

  if (loading) return <div className="p-10 text-center text-muted-foreground">Carregando…</div>;
  if (!praticante) return <div className="p-10 text-center text-muted-foreground">Praticante não encontrado</div>;

  const idade = calcIdade(praticante.data_nasc);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fotoUrl = praticante.foto_url ? `${backendUrl}${praticante.foto_url}` : null;

  const mensAtuais = mensalidades.filter((m) => {
    const now = new Date();
    return m.competencia === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  return (
    <div data-testid="praticante-detail-page" className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate("/praticantes")} data-testid="detail-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <p className="text-sm text-muted-foreground">Voltar para lista</p>
      </div>

      {/* Header card */}
      <Card className="border-border shadow-none rounded-2xl bg-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {fotoUrl ? (
              <img src={fotoUrl} alt={praticante.nome} className="h-24 w-24 rounded-2xl object-cover border-2 border-secondary" />
            ) : (
              <div className="h-24 w-24 rounded-2xl bg-secondary flex items-center justify-center text-2xl font-bold text-brand-turquoise">
                {(praticante.nome || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">{praticante.nome}</h1>
                <span className={`text-xs px-2 py-1 rounded-full ${TIPO_COLOR[praticante.tipo] || TIPO_COLOR.em_avaliacao}`}>
                  {praticante.tipo === "em_avaliacao" ? "Em avaliação" : praticante.tipo === "ativo" ? "Ativo" : praticante.tipo === "lista_espera" ? "Lista de espera" : "Inativo"}
                </span>
                {praticante.valor_mensal && <Badge variant="secondary">{brl(praticante.valor_mensal)}/mês</Badge>}
                {praticante.status_pos_avaliacao && (
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_POS_LABELS[praticante.status_pos_avaliacao]?.color || "bg-slate-100"}`} data-testid="detail-status-pos-badge">
                    {STATUS_POS_LABELS[praticante.status_pos_avaliacao]?.label || praticante.status_pos_avaliacao}
                  </span>
                )}
              </div>
              {canEdit && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Status pós-avaliação:</span>
                  <Select value={praticante.status_pos_avaliacao || ""} onValueChange={updateStatusPos}>
                    <SelectTrigger className="h-8 w-64 text-xs" data-testid="detail-status-pos-select">
                      <SelectValue placeholder="Definir status pós-avaliação…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Vai iniciar tratamento</SelectItem>
                      <SelectItem value="aguardando_resposta">Aguardando resposta da família</SelectItem>
                      <SelectItem value="nao_vai_iniciar">Não vai iniciar (desistiu)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <InfoLine label="Idade" value={idade != null ? `${idade} anos` : "—"} />
                <InfoLine label="Data nasc." value={praticante.data_nasc || "—"} />
                <InfoLine label="CPF" value={praticante.cpf || "—"} />
                <InfoLine label="Diagnóstico" value={praticante.diagnostico || "—"} />
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                {praticante.telefone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{praticante.telefone}</span>}
                {praticante.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{praticante.email}</span>}
                {praticante.responsavel?.nome && (
                  <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />Resp: {praticante.responsavel.nome}{praticante.responsavel.parentesco ? ` (${praticante.responsavel.parentesco})` : ""}</span>
                )}
              </div>
            </div>
            {canEdit && (
              <div className="flex flex-col gap-2">
                <Button variant="outline" onClick={() => navigate(`/praticantes?edit=${id}`)} data-testid="detail-editar-cadastro-btn">
                  <Pencil className="h-4 w-4 mr-1" /> Editar cadastro
                </Button>
                {canViewFicha && (
                  <Button variant="outline" onClick={() => navigate(`/praticantes/${id}/ficha`)} data-testid="detail-ficha-btn">
                    <ClipboardList className="h-4 w-4 mr-1" /> Ficha de avaliação
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="resumo">
        <TabsList>
          <TabsTrigger value="resumo" data-testid="tab-resumo">Resumo</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
          <TabsTrigger value="sessoes" data-testid="tab-sessoes">Próximas sessões ({sessoes.length})</TabsTrigger>
          {canViewFicha && <TabsTrigger value="evolucoes" data-testid="tab-evolucoes">Evoluções ({evolucoes.length})</TabsTrigger>}
          {canViewFin && <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>}
        </TabsList>

        <TabsContent value="resumo" className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Próximas sessões */}
          <Card className="border-border shadow-none rounded-xl bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-slate-800">Próximas sessões</h3>
                <CalendarDays className="h-4 w-4 text-brand-turquoise" />
              </div>
              {sessoes.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">Nenhuma sessão futura</p>
              ) : (
                <ul className="space-y-2">
                  {sessoes.slice(0, 4).map((s) => (
                    <li key={s.id} className="text-sm flex items-center gap-2 py-1.5 border-b border-border last:border-0">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">{s.tipo === "avaliacao" ? "AV" : "S"}</span>
                      <span className="text-slate-700">{s.inicio.slice(0, 10)} · {s.inicio.slice(11, 16)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Últimas evoluções */}
          {canViewFicha && (
            <Card className="border-border shadow-none rounded-xl bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-slate-800">Últimas evoluções</h3>
                  <ClipboardList className="h-4 w-4 text-brand-lilac" />
                </div>
                {evolucoes.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-6 text-center">Sem evoluções registradas</p>
                ) : (
                  <ul className="space-y-2">
                    {evolucoes.slice(0, 4).map((e) => (
                      <li key={e.id} className="text-sm py-1.5 border-b border-border last:border-0">
                        <p className="font-medium text-slate-700">{e.data}</p>
                        <p className="text-xs text-muted-foreground truncate">{e.atividade_realizada || e.observacoes || "—"}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}

          {/* Financeiro do mês */}
          {canViewFin && (
            <Card className="border-border shadow-none rounded-xl bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-slate-800">Situação — mês atual</h3>
                  <Wallet className="h-4 w-4 text-emerald-600" />
                </div>
                {mensAtuais.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-6 text-center">Sem mensalidade neste mês</p>
                ) : (
                  <ul className="space-y-2">
                    {mensAtuais.map((m) => (
                      <li key={m.id} className="text-sm flex items-center justify-between py-1.5 border-b border-border last:border-0">
                        <div>
                          <p className="font-medium">{brl(m.valor)}</p>
                          <p className="text-xs text-muted-foreground">Venc: {m.vencimento}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${STATUS_MENS[m.status]}`}>{m.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          <TimelineHistorico
            sessoes={sessoesAll}
            evolucoes={evolucoes}
            mensalidades={mensalidades}
            canViewFicha={canViewFicha}
            canViewFin={canViewFin}
          />
        </TabsContent>

        <TabsContent value="sessoes" className="mt-4">
          <Card className="border-border shadow-none rounded-xl bg-white">
            <CardContent className="p-0">
              {sessoes.length === 0 ? <div className="p-6"><EmptyState icon={CalendarDays} title="Nenhuma sessão futura" /></div>
                : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Horário</TableHead><TableHead>Tipo</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {sessoes.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.inicio.slice(0, 10)}</TableCell>
                          <TableCell>{s.inicio.slice(11, 16)}–{s.fim.slice(11, 16)}</TableCell>
                          <TableCell><Badge variant="secondary">{s.tipo}</Badge></TableCell>
                          <TableCell>{s.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {canViewFicha && (
          <TabsContent value="evolucoes" className="mt-4">
            <Card className="border-border shadow-none rounded-xl bg-white">
              <CardContent className="p-0">
                {evolucoes.length === 0 ? <div className="p-6"><EmptyState icon={ClipboardList} title="Sem evoluções" description="Registros clínicos aparecerão aqui." /></div>
                  : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Estado</TableHead><TableHead>Montaria</TableHead><TableHead>Participação</TableHead><TableHead>Atividade</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {evolucoes.map((e) => (
                          <TableRow key={e.id}>
                            <TableCell>{e.data}</TableCell>
                            <TableCell><Badge variant="secondary">{e.estado_geral || "—"}</Badge></TableCell>
                            <TableCell>{e.montaria || "—"}</TableCell>
                            <TableCell>{e.participacao != null ? `${e.participacao}/4` : "—"}</TableCell>
                            <TableCell className="text-muted-foreground truncate max-w-xs">{e.atividade_realizada || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canViewFin && (
          <TabsContent value="financeiro" className="mt-4">
            <Card className="border-border shadow-none rounded-xl bg-white">
              <CardContent className="p-0">
                {mensalidades.length === 0 ? <div className="p-6"><EmptyState icon={Wallet} title="Sem mensalidades" /></div>
                  : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Competência</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead><TableHead>Pago em</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {mensalidades.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>{m.competencia}</TableCell>
                            <TableCell>{m.vencimento}</TableCell>
                            <TableCell>{brl(m.valor)}</TableCell>
                            <TableCell><span className={`text-xs px-2 py-1 rounded-full ${STATUS_MENS[m.status]}`}>{m.status}</span></TableCell>
                            <TableCell className="text-muted-foreground">{m.pago_em || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
      <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
    </div>
  );
}

// ------ Timeline Histórico -----------------------------------------------
const TIPO_META = {
  sessao: { label: "Sessão", color: "bg-brand-turquoise", ring: "ring-brand-turquoise/20", icon: Activity },
  avaliacao: { label: "Avaliação", color: "bg-brand-lilac", ring: "ring-brand-lilac/20", icon: ClipboardList },
  evolucao: { label: "Evolução clínica", color: "bg-[#7B569A]", ring: "ring-[#7B569A]/20", icon: ClipboardList },
  pagamento: { label: "Mensalidade", color: "bg-emerald-600", ring: "ring-emerald-600/20", icon: DollarSign },
};
const STATUS_SESSAO_LABEL = {
  agendada: "Agendada",
  confirmada: "Confirmada",
  realizada: "Realizada",
  cancelada: "Cancelada",
  reagendada: "Reagendada",
  faltou: "Faltou",
};

function buildTimelineEvents({ sessoes, evolucoes, mensalidades, canViewFicha, canViewFin }) {
  const events = [];
  for (const s of sessoes || []) {
    const iso = s.inicio || "";
    events.push({
      id: `s-${s.id}`,
      when: iso,
      dateKey: iso.slice(0, 10),
      timeStr: iso.slice(11, 16),
      tipo: s.tipo === "avaliacao" ? "avaliacao" : "sessao",
      status: s.status,
      title: s.tipo === "avaliacao" ? "Avaliação clínica" : "Sessão de equoterapia",
      subtitle: s.terapeuta_nome ? `Terapeuta: ${s.terapeuta_nome}` : (s.observacoes || ""),
      raw: s,
    });
  }
  if (canViewFicha) {
    for (const e of evolucoes || []) {
      events.push({
        id: `e-${e.id}`,
        when: e.data || "",
        dateKey: (e.data || "").slice(0, 10),
        timeStr: "",
        tipo: "evolucao",
        title: "Evolução clínica registrada",
        subtitle: e.atividade_realizada || e.observacoes || (e.montaria ? `Montaria: ${e.montaria}` : ""),
        extra: {
          estado: e.estado_geral,
          participacao: e.participacao,
        },
        raw: e,
      });
    }
  }
  if (canViewFin) {
    for (const m of mensalidades || []) {
      const iso = m.pago_em || m.vencimento || "";
      events.push({
        id: `m-${m.id}`,
        when: iso,
        dateKey: iso.slice(0, 10),
        timeStr: "",
        tipo: "pagamento",
        title: m.status === "pago" ? "Mensalidade paga" : m.status === "atrasado" ? "Mensalidade em atraso" : m.status === "cancelado" ? "Mensalidade cancelada" : "Mensalidade pendente",
        subtitle: `Competência ${m.competencia} · Venc: ${m.vencimento}`,
        valor: m.valor,
        status: m.status,
        raw: m,
      });
    }
  }
  // Ordena do mais recente para o mais antigo
  events.sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  return events;
}

function groupByDate(events) {
  const groups = new Map();
  for (const ev of events) {
    const key = ev.dateKey || "sem-data";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ev);
  }
  return Array.from(groups.entries());
}

function formatDateBR(iso) {
  if (!iso || iso === "sem-data") return "Sem data";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function TimelineHistorico({ sessoes, evolucoes, mensalidades, canViewFicha, canViewFin }) {
  const [filter, setFilter] = useState("todos");
  const events = buildTimelineEvents({ sessoes, evolucoes, mensalidades, canViewFicha, canViewFin });
  const filtered = filter === "todos" ? events : events.filter((e) => {
    if (filter === "sessoes") return e.tipo === "sessao" || e.tipo === "avaliacao";
    if (filter === "evolucoes") return e.tipo === "evolucao";
    if (filter === "pagamentos") return e.tipo === "pagamento";
    return true;
  });
  const groups = groupByDate(filtered);
  const today = new Date().toISOString().slice(0, 10);

  const filters = [
    { key: "todos", label: `Todos (${events.length})` },
    { key: "sessoes", label: `Sessões (${events.filter((e) => e.tipo === "sessao" || e.tipo === "avaliacao").length})` },
  ];
  if (canViewFicha) filters.push({ key: "evolucoes", label: `Evoluções (${events.filter((e) => e.tipo === "evolucao").length})` });
  if (canViewFin) filters.push({ key: "pagamentos", label: `Pagamentos (${events.filter((e) => e.tipo === "pagamento").length})` });

  return (
    <Card className="border-border shadow-none rounded-xl bg-white" data-testid="timeline-card">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-brand-lilac" />
            <h3 className="font-heading font-semibold text-lg text-slate-800">Histórico completo</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.key ? "bg-brand-turquoise text-white border-brand-turquoise" : "bg-white text-slate-600 border-slate-200 hover:border-brand-turquoise"}`}
                data-testid={`timeline-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-12">
            <EmptyState icon={History} title="Sem registros no histórico" description="Os eventos aparecerão aqui conforme forem sendo registrados." />
          </div>
        ) : (
          <div className="relative">
            {/* Linha vertical */}
            <div className="absolute left-4 top-2 bottom-2 w-px bg-gradient-to-b from-brand-turquoise via-brand-lilac to-brand-gold opacity-30" aria-hidden />

            <div className="space-y-6">
              {groups.map(([dateKey, evs]) => (
                <div key={dateKey} className="relative">
                  <div className="flex items-center gap-3 mb-3 ml-9">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                      {formatDateBR(dateKey)}
                    </p>
                    {dateKey === today && (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-brand-turquoise bg-brand-turquoise/10 px-2 py-0.5 rounded-full">Hoje</span>
                    )}
                  </div>

                  <ul className="space-y-3">
                    {evs.map((ev) => {
                      const meta = TIPO_META[ev.tipo] || TIPO_META.sessao;
                      const Icon = meta.icon;
                      const isCancelada = ev.status === "cancelada" || ev.status === "faltou";
                      return (
                        <li key={ev.id} className="relative pl-12" data-testid={`timeline-item-${ev.id}`}>
                          <span className={`absolute left-1.5 top-1.5 flex items-center justify-center h-5 w-5 rounded-full ${meta.color} text-white ring-4 ${meta.ring} shadow-sm`}>
                            <Icon className="h-3 w-3" />
                          </span>
                          <div className={`rounded-xl border border-border bg-white p-4 hover:border-brand-turquoise/40 hover:shadow-sm transition ${isCancelada ? "opacity-60" : ""}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-slate-800 text-sm">{ev.title}</p>
                                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                                    {meta.label}
                                  </span>
                                  {ev.status && (ev.tipo === "sessao" || ev.tipo === "avaliacao") && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                                      ev.status === "realizada" || ev.status === "confirmada" ? "bg-emerald-100 text-emerald-700" :
                                      ev.status === "cancelada" || ev.status === "faltou" ? "bg-rose-100 text-rose-700" :
                                      ev.status === "reagendada" ? "bg-amber-100 text-amber-700" :
                                      "bg-slate-100 text-slate-600"
                                    }`}>
                                      {STATUS_SESSAO_LABEL[ev.status] || ev.status}
                                    </span>
                                  )}
                                  {ev.tipo === "pagamento" && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                                      ev.status === "pago" ? "bg-emerald-100 text-emerald-700" :
                                      ev.status === "atrasado" ? "bg-rose-100 text-rose-700" :
                                      ev.status === "cancelado" ? "bg-slate-100 text-slate-500" :
                                      "bg-amber-100 text-amber-700"
                                    }`}>
                                      {ev.status}
                                    </span>
                                  )}
                                </div>
                                {ev.subtitle && (
                                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{ev.subtitle}</p>
                                )}
                                {ev.extra?.estado && (
                                  <p className="text-[11px] text-slate-500 mt-1">Estado geral: <span className="font-medium">{ev.extra.estado}</span>{ev.extra.participacao != null && ` · Participação: ${ev.extra.participacao}/4`}</p>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                {ev.timeStr && <p className="text-xs font-mono text-slate-500">{ev.timeStr}</p>}
                                {ev.valor != null && (
                                  <p className="text-sm font-semibold text-slate-800">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(ev.valor)}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
